import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isSupabaseAvailable, dbToClinician } from '../services/supabaseClient';
import { Clinician, UserRole } from '../types';

/**
 * Hook to get the current clinician profile from Supabase.
 * Subscribes to realtime changes on the clinician's own row so that
 * fields like evolutivos_enabled update instantly without a page reload.
 */
export function useCurrentClinician() {
  const { user } = useAuth();
  const [clinician, setClinician] = useState<Clinician | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const clinicianIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user) {
      setClinician(null);
      setLoading(false);
      return;
    }

    // If Supabase not available, only use mock in development
    if (!isSupabaseAvailable()) {
      if (!import.meta.env.DEV) {
        setClinician(null);
        setError('Supabase no disponible');
        setLoading(false);
        return;
      }

      const mockClinician: Clinician = {
        id: user.id,
        userId: user.id,
        name: user.email?.split('@')[0] || 'Usuario',
        email: user.email || '',
        role: UserRole.CLINICIAN,
        active: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setClinician(mockClinician);
      setLoading(false);
      return;
    }

    async function fetchClinician() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase!
          .from('clinicians')
          .select('*')
          .eq('user_id', user!.id)
          .single();

        if (fetchError) {
          console.error('Error fetching clinician:', fetchError);
          setError(fetchError.message);
          setLoading(false);
          return;
        }

        if (data) {
          clinicianIdRef.current = data.id;
          setClinician(dbToClinician(data));
        } else {
          console.warn('Clinician not found for user:', user!.id);
          setError('Clinician profile not found');
        }

        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch clinician:', err);
        setError('Failed to load clinician profile');
        setLoading(false);
      }
    }

    fetchClinician();

    // Realtime subscription: update local state when the clinician's own row changes
    // (e.g. superadmin toggles evolutivos_enabled)
    const channel = supabase!
      .channel(`clinician-profile-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'clinicians',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.new) {
            setClinician(dbToClinician(payload.new as Parameters<typeof dbToClinician>[0]));
          }
        }
      )
      .subscribe();

    return () => {
      supabase!.removeChannel(channel);
    };
  }, [user]);

  return { clinician, loading, error };
}
